import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Container,
  IconButton,
  Box,
  TextField,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Snackbar,
  Alert,
  Tooltip,
  Chip
} from '@mui/material';
import { 
  ArrowBack, 
  Search, 
  Edit, 
  Sync,
  CheckCircle,
  Cancel,
  Star,
  StarBorder,
  Inventory
} from '@mui/icons-material';
import Loader from './Loader';
import { db } from './Dexie';
import { useLiveQuery } from 'dexie-react-hooks';
import { log } from '../services/loggingService';
import { syncTechyShopProducts, editTechyShopProduct, addProdotto } from '../services/productService';
import ProductEditModal from './ProductEditModal';

function TechyShopProduct() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  // Get products from Dexie
  const products = useLiveQuery(() => db.techyshop_prodotti.toArray(), []);

  // Auto-sync on mount
  useEffect(() => {
    const autoSync = async () => {
      setIsSyncing(true);
      await syncTechyShopProducts(false);
      setIsSyncing(false);
    };
    autoSync();
  }, []);

  // Filtered products
  const filteredProducts = useMemo(() => {
    if (!products) return [];
    
    const lowercasedQuery = searchQuery.toLowerCase();
    
    return products.filter(p => {
      const nome = p.nome || '';
      const sku = p.sku || '';
      const id = p.id ? p.id.toString() : '';
      
      return id.includes(lowercasedQuery) ||
             nome.toLowerCase().includes(lowercasedQuery) ||
             sku.toLowerCase().includes(lowercasedQuery);
    });
  }, [searchQuery, products]);

  const handleOpenModal = (product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  const handleAddProduct = () => {
    setSelectedProduct(null); // Passing null for new product
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  const handleSaveProduct = async (productData) => {
    setIsSaving(true);
    try {
      let result;
      if (productData.id) {
        result = await editTechyShopProduct(productData);
        if (result.success) {
          handleCloseModal();
        }
      } else {
        result = await addProdotto(productData);
        if (result.success) {
          // If product created, we need to sync to get the new product in Dexie
          await syncTechyShopProducts(true);
          
          // Get the new product from Dexie to transition to "Edit" mode
          const newProduct = await db.techyshop_prodotti.get(result.product_id);
          if (newProduct) {
            setSelectedProduct(newProduct);
            // We don't close the modal here, so the user can continue with variants/media
          } else {
            handleCloseModal();
          }
        }
      }

      setToast({ 
        open: true, 
        message: result.message, 
        severity: result.success ? 'success' : 'error' 
      });
    } catch (error) {
      log("Errore nel salvataggio del prodotto:", 'ERROR', error);
      setToast({ open: true, message: `Errore: ${error.message}`, severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSync = async (force = false) => {
    setIsSyncing(true);
    setToast({ open: true, message: force ? 'Forzo sincronizzazione completa...' : 'Sincronizzazione in corso...', severity: 'info' });
    
    try {
      const result = await syncTechyShopProducts(force);
      setToast({ 
        open: true, 
        message: result.message, 
        severity: result.success ? 'success' : 'error' 
      });
    } catch (error) {
      log("Errore durante la sincronizzazione prodotti:", 'ERROR', error);
      setToast({ open: true, message: `Errore: ${error.message}`, severity: 'error' });
    } finally {
      setIsSyncing(false);
    }
  };

  const handleToastClose = () => setToast({ ...toast, open: false });

  if (!products && isSyncing) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Loader />
        <Typography sx={{ mt: 2 }}>Inizializzazione catalogo prodotti...</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, pb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={() => navigate('/techyshop')} aria-label="back">
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1" sx={{ ml: 2, flexGrow: 1, fontWeight: 700 }}>
          Catalogo Prodotti
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<Edit />} 
            onClick={handleAddProduct}
          >
            Aggiungi Prodotto
          </Button>
          <Button 
            variant="outlined" 
            color="warning" 
            startIcon={<Sync />} 
            onClick={() => handleSync(true)}
            disabled={isSyncing}
          >
            Forza Sincronizzazione
          </Button>
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Cerca per nome, SKU o ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ backgroundColor: 'rgba(255,255,255,0.02)' }}
        />
      </Box>

      <TableContainer component={Paper} sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>ID</TableCell>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>SKU</TableCell>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>Nome Prodotto</TableCell>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }} align="right">Prezzo</TableCell>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }} align="center">Stock</TableCell>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }} align="center">Stato</TableCell>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }} align="right">Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProducts.map((p) => (
              <TableRow 
                key={p.id} 
                hover 
                onClick={() => handleOpenModal(p)}
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' } 
                }}
              >
                <TableCell sx={{ color: 'rgba(255,255,255,0.5)' }}>{p.id}</TableCell>
                <TableCell sx={{ color: 'rgba(255,255,255,0.7)', fontFamily: 'monospace' }}>{p.sku}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {p.nome}
                    {p.in_evidenza == 1 && (
                      <Tooltip title="In Evidenza">
                        <Star sx={{ color: '#ffc107', fontSize: 16 }} />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ color: 'white' }}>
                  {p.in_sconto == 1 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                      <Typography variant="caption" sx={{ textDecoration: 'line-through', opacity: 0.5 }}>
                        €{parseFloat(p.prezzo).toFixed(2)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#f44336', fontWeight: 'bold' }}>
                        €{parseFloat(p.prezzo_scontato).toFixed(2)}
                      </Typography>
                    </Box>
                  ) : (
                    `€${parseFloat(p.prezzo).toFixed(2)}`
                  )}
                </TableCell>
                <TableCell align="center">
                  <Chip 
                    label={p.quantita} 
                    size="small" 
                    icon={<Inventory sx={{ fontSize: '14px !important' }} />}
                    color={p.quantita > 0 ? "success" : "error"}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title={p.visibile == 1 ? "Visibile" : "Nascosto"}>
                    {p.visibile == 1 ? <CheckCircle style={{ color: '#4dab56' }} fontSize="small" /> : <Cancel style={{ color: '#f44336' }} fontSize="small" />}
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  <IconButton 
                    size="small" 
                    color="primary" 
                    onClick={(e) => { e.stopPropagation(); handleOpenModal(p); }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {filteredProducts.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                  <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                    {isSyncing ? 'Sincronizzazione prodotti...' : 'Nessun prodotto trovato.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modal Edit Prodotto */}
      <ProductEditModal 
        open={isModalOpen} 
        onClose={handleCloseModal} 
        product={selectedProduct} 
        onSave={handleSaveProduct}
        isSaving={isSaving}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleToastClose} severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default TechyShopProduct;
